import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, ChevronDown, Zap, GraduationCap, Users, Info, LogIn, Sparkles, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import CartModal from './CartModal';
import { SITE } from '../lib/site';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, goToCheckout } = useCart();
  const [fashionDropdownOpen, setFashionDropdownOpen] = useState(false);
  const [sportsDropdownOpen, setSportsDropdownOpen] = useState(false);
  const [academyDropdownOpen, setAcademyDropdownOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isStudent, setIsStudent] = useState(false);

  // Check if user is logged in as a student
  useEffect(() => {
    const checkStudentStatus = () => {
      const token = localStorage.getItem('academy_token');
      if (token) {
        try {
          // Decode JWT to check is_student (basic decode, not verification)
          const payload = JSON.parse(atob(token.split('.')[1]));
          // Also check localStorage for cached user data
          const userData = localStorage.getItem('academy_user');
          if (userData) {
            const user = JSON.parse(userData);
            setIsStudent(user.is_student === true);
          }
        } catch (e) {
          setIsStudent(false);
        }
      } else {
        setIsStudent(false);
      }
    };
    
    checkStudentStatus();
    // Listen for storage changes (login/logout from other tabs)
    window.addEventListener('storage', checkStudentStatus);
    return () => window.removeEventListener('storage', checkStudentStatus);
  }, [location]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const getCartCount = () => {
    if (!cart || !cart.lineItems) return 0;
    return cart.lineItems.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <>
      <header className="fixed top-0 w-full z-[100] bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
      <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 max-w-full overflow-visible">
        <div className="flex items-center justify-between">
          {/* Logo - PRSVR or Scholar Athlete based on host */}
          <a href="/" className="flex items-center space-x-2 flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
              alt={SITE.brand} 
              className="h-10 sm:h-12 w-auto brightness-0 invert"
            />
            {SITE.isSchool && (
              <span className="hidden sm:block text-cream font-black text-xs uppercase tracking-[0.2em] border-l border-warmSage/30 pl-2 ml-1">
                Scholar Athlete<br/>Academy
              </span>
            )}
          </a>

          {/* Navigation - Hidden on mobile, shown on md+ */}
          <div className="hidden md:flex items-center gap-4 lg:gap-8">
            {/* Academy Dropdown - First Position */}
            {isStudent ? (
              <a 
                href="/academy/school"
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-black uppercase text-sm tracking-wider flex items-center gap-2 transition-all shadow-lg shadow-orange-600/30 hover:shadow-orange-600/50"
              >
                <Zap className="h-4 w-4" />
                MY SCHOOL
              </a>
            ) : (
              <div 
                className="relative group"
                onMouseEnter={() => setAcademyDropdownOpen(true)}
                onMouseLeave={() => {
                  // Debounce: 400ms delay before closing
                  setTimeout(() => {
                    const dropdown = document.querySelector('[data-dropdown="academy"]');
                    const trigger = document.querySelector('[data-trigger="academy"]');
                    if (dropdown && !dropdown.matches(':hover') && trigger && !trigger.matches(':hover')) {
                      setAcademyDropdownOpen(false);
                    }
                  }, 400);
                }}
              >
                <button 
                  className="text-cream hover:text-orange-500 transition-colors flex items-center gap-1 font-bold uppercase text-sm tracking-wider py-2"
                  data-testid="academy-dropdown-btn"
                  data-trigger="academy"
                >
                  ACADEMY
                  <ChevronDown className="h-4 w-4" />
                </button>
                
                {/* Invisible bridge to close the gap */}
                {academyDropdownOpen && (
                  <div className="absolute top-full left-0 w-full h-3 bg-transparent" />
                )}
                
                {academyDropdownOpen && (
                  <div 
                    className="absolute top-full left-0 mt-1 w-72 bg-charcoal border-2 border-orange-500/50 rounded-lg shadow-xl py-2 overflow-hidden z-50"
                    data-dropdown="academy"
                    onMouseEnter={() => setAcademyDropdownOpen(true)}
                    onMouseLeave={() => {
                      setTimeout(() => setAcademyDropdownOpen(false), 300);
                    }}
                  >
                    {/* Scholar Athlete Academy - Main Header (Clickable) */}
                    <a 
                      href="/academy" 
                      className="block px-4 py-2 hover:bg-orange-500/20 transition-colors"
                      data-testid="saa-main-link"
                    >
                      <p className="text-orange-500 font-black text-sm uppercase tracking-wider hover:text-orange-400">
                        Scholar Athlete Academy
                      </p>
                    </a>
                    {/* Athlete Lab - Sub item */}
                    <a 
                      href="/academy" 
                      className="flex items-center gap-3 px-4 py-2 ml-2 text-cream hover:bg-orange-500/20 transition-colors group"
                      data-testid="scholar-athlete-link"
                    >
                      <GraduationCap className="h-4 w-4 text-orange-500" />
                      <span className="text-sm text-gray-300 group-hover:text-orange-400">Athlete Lab</span>
                    </a>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-700 my-2"></div>
                    
                    {/* Scholar Parent Academy - Main Header (Clickable) */}
                    <a 
                      href="/academy?type=parent" 
                      className="block px-4 py-2 hover:bg-purple-500/20 transition-colors"
                      data-testid="spa-main-link"
                    >
                      <p className="text-purple-400 font-black text-sm uppercase tracking-wider hover:text-purple-300">
                        Scholar Parent Academy
                      </p>
                    </a>
                    {/* Executive Mirror - Sub item */}
                    <a 
                      href="/academy?type=parent" 
                      className="flex items-center gap-3 px-4 py-2 ml-2 text-cream hover:bg-purple-500/20 transition-colors group"
                      data-testid="scholar-parent-link"
                    >
                      <Users className="h-4 w-4 text-purple-500" />
                      <span className="text-sm text-gray-300 group-hover:text-purple-400">Executive Mirror</span>
                    </a>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-700 my-2"></div>
                    
                    {/* L'Chaim - Light Blue Branding */}
                    <a 
                      href="/academy/lchaim" 
                      className="block px-4 py-2 hover:bg-cyan-500/20 transition-colors"
                      data-testid="lchaim-main-link"
                    >
                      <p className="text-cyan-400 font-black text-sm uppercase tracking-wider hover:text-cyan-300">
                        L'Chaim: The 13th Year
                      </p>
                    </a>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-700 my-2"></div>
                    
                    {/* Electives */}
                    <a 
                      href="/academy/electives" 
                      className="flex items-center gap-3 px-4 py-2 text-cream hover:bg-orange-500/20 transition-colors"
                      data-testid="electives-link"
                    >
                      <Sparkles className="h-4 w-4 text-orange-500" />
                      <span className="text-sm text-gray-300 hover:text-orange-400">Spring '26 Electives</span>
                    </a>
                    
                    {/* About The Academy */}
                    <a 
                      href="/academy/about" 
                      className="flex items-center gap-3 px-4 py-2 text-cream hover:bg-orange-500/20 transition-colors"
                      data-testid="about-academy-link"
                    >
                      <GraduationCap className="h-4 w-4 text-orange-500" />
                      <span className="text-sm text-gray-300 hover:text-orange-400">About The Academy</span>
                    </a>
                    
                    {/* Member Login */}
                    <a 
                      href="/academy/login" 
                      className="flex items-center gap-3 px-4 py-2 text-cream hover:bg-orange-500/20 transition-colors"
                      data-testid="member-login-link"
                    >
                      <LogIn className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-300 hover:text-white">Member Login</span>
                    </a>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-700 my-2"></div>
                    
                    {/* Official Apparel Link */}
                    <a 
                      href="/shop" 
                      className="flex items-center gap-3 px-4 py-2 text-warmSage hover:bg-warmSage/20 transition-colors"
                      data-testid="official-apparel-link"
                    >
                      <ShoppingCart className="h-4 w-4 text-warmSage" />
                      <span className="text-sm font-bold text-warmSage hover:text-cream">Official Apparel</span>
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Fashion Dropdown — hidden on school host */}
            {!SITE.isSchool && (
            <div 
              className="relative group"
              onMouseEnter={() => setFashionDropdownOpen(true)}
              onMouseLeave={() => {
                setTimeout(() => {
                  const dropdown = document.querySelector('[data-dropdown="fashion"]');
                  const trigger = document.querySelector('[data-trigger="fashion"]');
                  if (dropdown && !dropdown.matches(':hover') && trigger && !trigger.matches(':hover')) {
                    setFashionDropdownOpen(false);
                  }
                }, 400);
              }}
            >
              <button 
                className="text-cream hover:text-warmSage transition-colors flex items-center gap-1 font-bold uppercase text-sm tracking-wider py-2"
                data-trigger="fashion"
              >
                FASHION
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {fashionDropdownOpen && (
                <div 
                  className="absolute top-full left-0 pt-3 -mt-1 w-48 z-50"
                  data-dropdown="fashion"
                  onMouseEnter={() => setFashionDropdownOpen(true)}
                  onMouseLeave={() => setTimeout(() => setFashionDropdownOpen(false), 300)}
                >
                <div className="bg-charcoal border-2 border-warmSage rounded-lg shadow-xl py-2">
                  <a 
                    href="/shop" 
                    className="block px-4 py-2 text-cream hover:bg-warmSage hover:text-charcoal transition-colors font-bold text-sm"
                  >
                    ALL PRODUCTS
                  </a>
                  <a 
                    href="/celebrities" 
                    className="block px-4 py-2 text-cream hover:bg-warmSage hover:text-charcoal transition-colors font-bold text-sm"
                  >
                    CELEBRITIES IN PRSVR
                  </a>
                  <a 
                    href="/collections/footwear" 
                    className="block px-4 py-2 text-cream hover:bg-warmSage hover:text-charcoal transition-colors font-bold text-sm"
                  >
                    FOOTWEAR
                  </a>
                  <a 
                    href="/collections/in-stock" 
                    className="block px-4 py-2 text-cream hover:bg-warmSage hover:text-charcoal transition-colors font-bold text-sm"
                  >
                    IN STOCK
                  </a>
                  <a 
                    href="/collections/suits-separates" 
                    className="block px-4 py-2 text-cream hover:bg-warmSage hover:text-charcoal transition-colors font-bold text-sm"
                  >
                    SUITS & SETS
                  </a>
                  <a 
                    href="/collections/valley-track-suit" 
                    className="block px-4 py-2 text-cream hover:bg-warmSage hover:text-charcoal transition-colors font-bold text-sm"
                  >
                    VALLEY TRACK SUIT
                  </a>
                  <a 
                    href="/collections" 
                    className="block px-4 py-2 text-cream hover:bg-warmSage hover:text-charcoal transition-colors font-bold text-sm border-t border-warmSage/30 mt-2"
                  >
                    ALL COLLECTIONS
                  </a>
                  {/* Academy Link */}
                  <a 
                    href="/academy" 
                    className="block px-4 py-2 text-orange-400 hover:bg-orange-500/20 hover:text-orange-300 transition-colors font-black text-sm border-t border-warmSage/30 mt-2 uppercase tracking-wider"
                  >
                    THE ACADEMY
                  </a>
                  {/* L'Chaim Link */}
                  <a 
                    href="/academy/lchaim" 
                    className="block px-4 py-2 text-cyan-400 hover:bg-cyan-500/20 hover:text-cyan-300 transition-colors font-black text-sm uppercase tracking-wider"
                    data-testid="lchaim-fashion-link"
                  >
                    L'CHAIM
                  </a>
                </div>
                </div>
              )}
            </div>
            )}

            {/* Sports Dropdown — hidden on school host */}
            {!SITE.isSchool && (
            <div 
              className="relative"
              onMouseEnter={() => setSportsDropdownOpen(true)}
              onMouseLeave={() => {
                setTimeout(() => {
                  const dropdown = document.querySelector('[data-dropdown="sports"]');
                  const trigger = document.querySelector('[data-trigger="sports"]');
                  if (dropdown && !dropdown.matches(':hover') && trigger && !trigger.matches(':hover')) {
                    setSportsDropdownOpen(false);
                  }
                }, 400);
              }}
            >
              <button 
                className="text-cream hover:text-warmSage transition-colors flex items-center gap-1 font-bold uppercase text-sm tracking-wider py-2"
                data-trigger="sports"
              >
                SPORTS
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {sportsDropdownOpen && (
                <div 
                  className="absolute top-full left-0 pt-3 -mt-1 w-48 z-50"
                  data-dropdown="sports"
                  onMouseEnter={() => setSportsDropdownOpen(true)}
                  onMouseLeave={() => setTimeout(() => setSportsDropdownOpen(false), 300)}
                >
                <div className="bg-charcoal border-2 border-warmSage rounded-lg shadow-xl py-2">
                  <a 
                    href="/collections/prsvr-sports" 
                    className="block px-4 py-2 text-cream hover:bg-warmSage hover:text-charcoal transition-colors font-bold text-sm"
                  >
                    SPORTS COLLECTION
                  </a>
                  <a 
                    href="/uniform-order" 
                    className="block px-4 py-2 text-cream hover:bg-warmSage hover:text-charcoal transition-colors font-bold text-sm"
                  >
                    CUSTOM UNIFORMS
                  </a>
                </div>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Mobile Menu Button + Cart - Always visible */}
          <div className="flex items-center gap-3">
            {/* Cart Button — hidden on school host */}
            {!SITE.isSchool && (
            <Button 
              onClick={() => setCartModalOpen(true)}
              className="bg-warmSage text-charcoal hover:bg-warmSage/90 font-bold relative flex-shrink-0"
              size="sm"
            >
              <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-charcoal text-cream w-5 h-5 sm:w-6 sm:h-6 rounded-full text-xs flex items-center justify-center font-bold">
                  {getCartCount()}
                </span>
              )}
            </Button>
            )}
            
            {/* Mobile Menu Toggle */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-cream p-2"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-warmSage/20 pt-4">
            <div className="flex flex-col space-y-4">
              {/* Academy Section */}
              <div className="space-y-2">
                <p className="text-orange-500 font-black text-xs uppercase tracking-wider">Academy</p>
                {isStudent ? (
                  <>
                    <a href="/academy/school" className="block text-orange-400 font-bold hover:text-orange-300 py-1 pl-2">
                      My School
                    </a>
                    <a href="/academy/lms" className="block text-cream hover:text-orange-400 py-1 pl-2">
                      Training Ground
                    </a>
                    <a href="/academy/high-performance" className="block text-cream hover:text-orange-400 py-1 pl-2">
                      Performance
                    </a>
                    <a href="/academy/lchaim" className="block text-cyan-400 hover:text-cyan-300 py-1 pl-2">
                      L'Chaim
                    </a>
                  </>
                ) : (
                  <>
                    <a href="/academy" className="block text-cream hover:text-orange-400 py-1 pl-2">Scholar Athlete Academy</a>
                    <a href="/academy?type=parent" className="block text-cream hover:text-purple-400 py-1 pl-2">Scholar Parent Academy</a>
                    <a href="/academy/lchaim" className="block text-cyan-400 hover:text-cyan-300 py-1 pl-2">L'Chaim: The 13th Year</a>
                    <a href="/academy/electives" className="block text-cream hover:text-orange-400 py-1 pl-2">Spring '26 Electives</a>
                    <a href="/academy/login" className="block text-gray-400 hover:text-white py-1 pl-2">Member Login</a>
                  </>
                )}
              </div>
              
              {/* Fashion Section — hidden on school host */}
              {!SITE.isSchool && (
              <div className="space-y-2">
                <p className="text-warmSage font-black text-xs uppercase tracking-wider">Fashion</p>
                <a href="/shop" className="block text-cream hover:text-warmSage py-1 pl-2">All Products</a>
                <a href="/celebrities" className="block text-cream hover:text-warmSage py-1 pl-2">Celebrities in PRSVR</a>
                <a href="/collections/footwear" className="block text-cream hover:text-warmSage py-1 pl-2">Footwear</a>
                <a href="/collections/in-stock" className="block text-cream hover:text-warmSage py-1 pl-2">In Stock</a>
                <a href="/collections/suits-separates" className="block text-cream hover:text-warmSage py-1 pl-2">Suits & Sets</a>
                <a href="/collections/valley-track-suit" className="block text-cream hover:text-warmSage py-1 pl-2">Valley Track Suit</a>
                <a href="/collections" className="block text-cream hover:text-warmSage py-1 pl-2">All Collections</a>
              </div>
              )}
              
              {/* Sports Section — hidden on school host */}
              {!SITE.isSchool && (
              <div className="space-y-2">
                <p className="text-warmSage font-black text-xs uppercase tracking-wider">Sports</p>
                <a href="/collections/prsvr-sports" className="block text-cream hover:text-warmSage py-1 pl-2">Sports Collection</a>
                <a href="/uniform-order" className="block text-cream hover:text-warmSage py-1 pl-2">Custom Uniforms</a>
              </div>
              )}
            </div>
          </div>
        )}
      </nav>
      
      {/* Cart Modal */}
      <CartModal 
        isOpen={cartModalOpen}
        onClose={() => setCartModalOpen(false)}
        cart={cart}
        goToCheckout={goToCheckout}
      />
    </header>
    </>
  );
};

export default Header;
